/**
 * Splits a CSV line into columns, respecting quoted values that may contain commas.
 * 
 * Why we need this: Simple split(',') would break on commas inside quoted values.
 * This is especially important for schain values which may be encapsulated with quotes (").
 * 
 * Examples:
 * - Input: 'key,"example.net,1000001|example.com,2000001"'
 *   Output: ['key', '"example.net,1000001|example.com,2000001"']
 *   Without this: ['key', '"example.net', '1000001|example.com', '2000001"'] ❌ WRONG
 * 
 * - Input: '123,"value,with,commas"'
 *   Output: ['123', '"value,with,commas"']
 *   Without this: ['123', '"value', 'with', 'commas"'] ❌ WRONG
 * 
 * - Input: 'key,schain' (no quotes)
 *   Output: ['key', 'schain']
 *   Works the same as simple split, but handles quotes too ✓
 * 
 * Note: Schain values encapsulated with quotes are properly extracted and can be
 * sanitized using sanitizeSchain() which removes the surrounding quotes.
 */
export function splitCSVLineRespectingQuotes(line: string): string[] {
  const columns: string[] = [];
  let column = '';
  let inQuotes = false;

  for (const char of line) {
    if (char === '"') {
      inQuotes = !inQuotes;
      column += char;
    } else if (char === ',' && !inQuotes) {
      columns.push(column.trim());
      column = '';
    } else {
      column += char;
    }
  }
  columns.push(column.trim());
  return columns;
}

/**
 * Normalizes CSV column headers:
 * - Converts to lowercase
 * - Trims leading/trailing whitespace
 */
export function normalizeHeader(header: string): string {
  return header.trim().toLowerCase();
}

/**
 * Normalizes an array of CSV column headers
 */
export function normalizeHeaders(headers: string[]): string[] {
  return headers.map(normalizeHeader);
}

/**
 * Finds the column index by matching against a list of accepted aliases
 */
export function findColumnIndex(normalizedHeaders: string[], aliases: string[]): number {
  return normalizedHeaders.findIndex(header => aliases.includes(header));
}

/**
 * Sanitizes key value: converts to lowercase and trims whitespace
 */
export function sanitizeKey(key: string): string {
  return key.trim().toLowerCase();
}

/**
 * Sanitizes schain value: trims whitespace only (preserves case)
 * 
 * Also handles schain values encapsulated with quotes (").
 * Removes surrounding quotes if present, then trims whitespace.
 * Normalizes separators by removing spaces around pipes (|) and commas (,).
 * 
 * Examples:
 * - Input: '  "example.net,1000001"  '
 *   Output: 'example.net,1000001'
 * 
 * - Input: '  XYZ 789  '
 *   Output: 'XYZ 789'
 * 
 * - Input: 'pubnative.net,1008060 | Fourthdimentionconsulting.com,19118818'
 *   Output: 'pubnative.net,1008060|Fourthdimentionconsulting.com,19118818'
 * 
 * - Input: '"fourthdimentionconsulting.com, 7689460"'
 *   Output: 'fourthdimentionconsulting.com,7689460'
 */
export function sanitizeSchain(schain: string): string {
  let sanitized = schain.trim();
  // Remove surrounding quotes if present
  if (sanitized.startsWith('"') && sanitized.endsWith('"')) {
    sanitized = sanitized.slice(1, -1).trim();
  }
  // Normalize pipe separators: remove spaces around pipes
  // Examples:
  // - Input: 'domain1,id1 | domain2,id2'
  //   Output: 'domain1,id1|domain2,id2'
  // - Input: 'example.net,1000001  |  example.com,2000001'
  //   Output: 'example.net,1000001|example.com,2000001'
  sanitized = sanitized.replace(/\s*\|\s*/g, '|');
  // Normalize comma separators: remove spaces before and after commas
  // Examples:
  // - Input: 'domain.com , 12345'
  //   Output: 'domain.com,12345'
  // - Input: 'example.net , 1000001'
  //   Output: 'example.net,1000001'
  sanitized = sanitized.replace(/\s*,\s*/g, ',');
  return sanitized;
}

/**
 * Validates a schain object (JSON format)
 */
export function validateSchainObject(schain: any): boolean {
  if (!schain.ver || !schain.hasOwnProperty('complete') || !schain.nodes) {
    console.error('Missing required schain fields: ver, complete, nodes');
    return false;
  }

  if (!Array.isArray(schain.nodes) || schain.nodes.length === 0) {
    console.error('Nodes must be a non-empty array');
    return false;
  }

  for (const node of schain.nodes) {
    if (!node.asi || !node.sid || !node.hasOwnProperty('hp')) {
      console.error('Invalid node structure: asi, sid, hp required');
      return false;
    }
  }

  return true;
}

/**
 * Validates pipe-delimited schain format: "1.0!exchange1.com!1234!1!publisher!pub001!"
 */
export function validatePipeDelimitedSchain(schain: string): boolean {
  const parts = schain.split('!');
  if (parts.length < 6) {
    console.error(`Invalid pipe format: expected 6+ parts, got ${parts.length}`);
    return false;
  }

  // Validate parts
  const [version, advertisingSystemIdentifier, sellerId, hops] = parts;

  if (!version || !advertisingSystemIdentifier || !sellerId || !hops) {
    console.error('Missing required parts: version, advertisingSystemIdentifier, sellerId, hops');
    return false;
  }

  if (isNaN(parseInt(hops))) {
    console.error('hops must be a number');
    return false;
  }

  return true;
}

/**
 * Validates comma-separated schain format: "pubnative.net,1009809|fourthdimentionconsulting.com,19118818"
 */
export function validateCommaSeparatedSchain(schain: string): boolean {
  // Basic validation for comma-separated format
  // Format: "domain1,id1|domain2,id2" or similar variations
  const parts = schain.split(',');
  if (parts.length < 2) {
    console.error(`Invalid comma format: expected 2+ parts, got ${parts.length}`);
    return false;
  }

  // Check that we have at least one domain-like part
  const hasDomain = parts.some(part => part.includes('.') || part.includes('|'));
  if (!hasDomain) {
    console.error('Comma-separated schain must contain domain or pipe-separated values');
    return false;
  }

  return true;
}

/**
 * Validates schain string in multiple formats (JSON, pipe-delimited, comma-separated)
 */
export function validateSchainString(schain: string): boolean {
  // Basic validation for multiple schain formats
  if (!schain || typeof schain !== 'string') {
    console.error('Schain is not a string');
    return false;
  }

  // Check if it's JSON format
  if (schain.startsWith('{') && schain.endsWith('}')) {
    try {
      const parsed = JSON.parse(schain);
      return validateSchainObject(parsed);
    } catch (error) {
      console.error('Invalid JSON format:', error);
      return false;
    }
  }

  // Check pipe-delimited format: "1.0!exchange1.com!1234!1!publisher!pub001!"
  if (schain.includes('!')) {
    return validatePipeDelimitedSchain(schain);
  }

  // Check comma-separated format: "pubnative.net,1009809|fourthdimentionconsulting.com,19118818"
  if (schain.includes(',')) {
    return validateCommaSeparatedSchain(schain);
  }

  console.error('Unknown schain format - must be JSON, pipe-delimited (!), or comma-separated (,)');
  return false;
}

/**
 * Converts list items to schains array format
 */
export function convertListItemsToSchains(listItems: string[]): any[] {
  const schains = [];
  listItems.forEach((item, index) => {
    if (validateSchainString(item)) {
      schains.push({
        key: `schain_${index + 1}`,
        schain: item // Send as string
      });
    } else {
      console.error('Invalid schain format:', item);
      schains.push({
        key: `invalid_${index + 1}`,
        schain: item // Send invalid string for server to handle
      });
    }
  });
  return schains;
}

/**
 * Validates and parses a single CSV line into a schain object
 * 
 * Handles schain values that may be encapsulated with quotes (").
 * The schain value is sanitized (quotes removed, whitespace trimmed) while preserving case.
 */
export function validateSchainLine(
  line: string,
  lineIndex: number,
  keyColumnIndex: number,
  schainColumnIndex: number,
  maxRowLength: number,
  maxNodes: number
): any | null {
  if (line.length > maxRowLength) {
    throw new Error(`Line ${lineIndex + 1} exceeds maximum length of ${maxRowLength} characters. Found ${line.length} characters.`);
  }

  const columns = splitCSVLineRespectingQuotes(line);
  if (columns.length !== 2) {
    console.warn('Skipping line with insufficient columns:', line);
    return null;
  }

  // Data value sanitization:
  // - key: convert to lowercase and trim whitespace
  // - schain: trim whitespace only (preserve case)
  const key = sanitizeKey(columns[keyColumnIndex]);
  let schainValue = sanitizeSchain(columns[schainColumnIndex]);

  // Validate node count
  const nodes = schainValue.split('|').filter(node => node.trim());
  if (nodes.length > maxNodes) {
    throw new Error(`Schain on line ${lineIndex + 1} exceeds maximum of ${maxNodes} nodes. Found ${nodes.length} nodes.`);
  }

  return {
    key: key,
    schain: schainValue !== null ? schainValue.trim() : ""
  };
}

/**
 * Parses a CSV file and returns an array of schain objects
 * 
 * Handles schain values that may be encapsulated with quotes (").
 * The parsing respects quoted values containing commas, and schain values
 * are sanitized to remove surrounding quotes while preserving case.
 */
export function parseSchainCSVFile(
  file: File,
  keyHeaderAliases: string[],
  schainHeaderAliases: string[],
  maxRowLength: number,
  maxNodes: number
): Promise<any[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e: any) => {
      try {
        const csvContent = e.target.result;
        const lines = csvContent.split('\n');
        const schains = [];

        // Validate header
        if (lines.length === 0) {
          reject(new Error('CSV file is empty'));
          return;
        }
        const headerLine = lines[0].trim();
        const headerColumns = normalizeHeaders(headerLine.split(','));

        if (headerColumns.length !== 2) {
          reject(new Error(`Invalid CSV header. Expected exactly 2 columns, found ${headerColumns.length} columns: "${headerLine}"`));
          return;
        }

        const keyColumnIndex = findColumnIndex(headerColumns, keyHeaderAliases);
        const schainColumnIndex = findColumnIndex(headerColumns, schainHeaderAliases);

        if (keyColumnIndex === -1 || schainColumnIndex === -1) {
          const missing = [];
          if (keyColumnIndex === -1) missing.push(`one of: ${keyHeaderAliases.join(', ')}`);
          if (schainColumnIndex === -1) missing.push(`one of: ${schainHeaderAliases.join(', ')}`);
          reject(new Error(`Invalid CSV header. Missing required columns: ${missing.join('; ')}. Found: "${headerLine}"`));
          return;
        }

        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue; // Skip empty lines

          try {
            const parsedSchain = validateSchainLine(line, i, keyColumnIndex, schainColumnIndex, maxRowLength, maxNodes);
            if (parsedSchain === null) {
              // Line was skipped (invalid format)
              continue;
            }
            schains.push(parsedSchain);
          } catch (error) {
            reject(error);
            return;
          }
        }
        resolve(schains);
      } catch (error) {
        console.error('CSV parsing error:', error);
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsText(file);
  });
}

